import { Box, Menu, MenuItem } from '@mui/material';
import {
  getCategory,
  impliedCostRate,
  ownHours,
  rateCardAllowedFor,
  setCategoryAmount,
  setOwnHours,
  setTmDefaultRate,
} from '@timelog/pm-shared';
import type { RateCardEntry } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryCard } from './CategoryCard';
import { parseHoursInput, useContractEditing, useFinancials } from './editor';
import { useGuidedEntry } from './flow';
import { RateCardEditor } from './RateCardEditor';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import type { CommitIntent } from '@/components/planning/cells';
import { InlineTextCell } from '@/components/planning/cells';
import { MICRO_LABEL_TRACKING } from '@/components/ui';
import { formatAmount, formatHours } from '@/lib';

/**
 * Own Work (span 2): amount, factor and hours are three independent user-owned
 * numbers (D11 — BR-12 retired). Hours are click-to-edit and, once typed, stay
 * exactly as typed; nothing derives the factor. What moves instead is the
 * cost per hour the three imply, shown inline next to the average rate
 * (CALC-23: `factor = avgRate / impliedCost`). T&M contracts get the "default
 * hourly rate" convenience — the PM picks one card entry and own amount =
 * hours × its rate; the pick is STORED (`tmDefaultRateEntryId`) and nothing is
 * selected until the PM chooses, so a seeded card never looks chosen — plus the
 * rates-per-role editor; a fixed-price contract carries no rate card at all
 * (BR-11/D10).
 */
export const OwnWorkCard = () => {
  const { state, dispatch, blendedCostRate } = useFinancials();
  const { isEditing, startEditing, stopEditing, advanceFrom } = useContractEditing();
  const guided = useGuidedEntry();
  const [rateAnchor, setRateAnchor] = useState<HTMLElement | null>(null);
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  const own = getCategory(state.document, 'own');
  const hours = ownHours(state.document, blendedCostRate);
  const avgRate = hours > 0 ? own.amount / hours : null;
  const impliedCost = impliedCostRate(state.document, blendedCostRate);
  const hoursTyped = own.ownHoursOverride !== undefined;
  const hasRateCard = rateCardAllowedFor(state.type);
  const isTm = state.type === 'tm';

  const hoursFlowActive = guided.activeStepFor('own') === 'ownHours';

  const commitHours = (raw: string, intent: CommitIntent = 'commit') => {
    const parsed = parseHoursInput(raw);
    if (hoursFlowActive) {
      guided.commit(parsed);
      return;
    }
    if (parsed !== null) {
      dispatch({ type: 'SET_OWN_HOURS', hours: parsed });
    }
    advanceFrom('own-hours', intent);
  };

  /** T&M convenience: own amount = current hours × the picked rate; the hours stay typed (D11). */
  const pickRate = (entry: RateCardEntry) => {
    setRateAnchor(null);
    dispatch({
      type: 'APPLY_OP',
      op: (document) => {
        const currentHours = ownHours(document, blendedCostRate);
        const repriced = setCategoryAmount(document, 'own', Math.round(currentHours * entry.rate));
        return setTmDefaultRate(setOwnHours(repriced, currentHours), entry.id);
      },
    });
  };

  const selectedRate = state.document.rateCard.find(
    (entry) => entry.id === state.document.tmDefaultRateEntryId,
  );

  return (
    <CategoryCard categoryId="own" span={2}>
      {/* hours (CALC-11; typed hours are the user's number — sticky, D11) */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <InlineTextCell
          automationId="own-hours"
          value={hours > 0 ? String(hours) : ''}
          editing={hoursFlowActive || isEditing('own-hours')}
          onStartEdit={() => startEditing('own-hours')}
          onCancel={hoursFlowActive ? guided.exit : stopEditing}
          advanceOnEnter={!hoursFlowActive}
          onCommit={(raw, intent) => {
            if (hoursFlowActive && intent === 'tab-next' && raw.trim() === '') {
              guided.skip();
              return;
            }
            commitHours(raw, intent);
          }}
          placeholder="0"
          inputWidth={90}
          inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 500 }}
          display={
            <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
              <Box
                component="span"
                sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 500 }}
              >
                {formatHours(hours)}
              </Box>
              <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                {t('pmCommon:unit.BudgetHoursLong')}
              </Box>
            </Box>
          }
        />
        {avgRate !== null ? (
          <Box
            component="span"
            data-automation-id="own-avg-rate"
            sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, color: 'var(--fg-3)' }}
          >
            {t('ownWork.AvgRate', { amount: formatAmount(Math.round(avgRate)) })}
          </Box>
        ) : null}
        {impliedCost !== undefined && !isTm ? (
          <Box
            component="span"
            data-automation-id="own-implied-cost"
            title={
              hoursTyped ? t('ownWork.ImpliedCostTitleTyped') : t('ownWork.ImpliedCostTitleDerived')
            }
            sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, color: 'var(--fg-3)' }}
          >
            {t('ownWork.ImpliedCost', { amount: formatAmount(Math.round(impliedCost)) })}
          </Box>
        ) : null}
      </Box>
      {hoursFlowActive ? (
        <Box sx={{ fontSize: 11, color: 'var(--fg-3)', mb: 1 }}>{t('ownWork.HintHours')}</Box>
      ) : null}

      {/* T&M: default hourly rate picked off the rate card */}
      {isTm ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box
            component="span"
            sx={{
              fontSize: 11,
              letterSpacing: MICRO_LABEL_TRACKING,
              textTransform: 'uppercase',
              color: 'var(--fg-3)',
              flex: 'none',
            }}
          >
            {t('pmCommon:term.DefaultHourlyRate')}
          </Box>
          <Box
            component="button"
            type="button"
            data-automation-id="own-rate-picker"
            onClick={(event: MouseEvent<HTMLElement>) => setRateAnchor(event.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              border: 'var(--border-width-thin) solid var(--border-divider)',
              borderRadius: 'var(--border-radius-pill)',
              bgcolor: 'var(--bg-panel)',
              fontSize: 11,
              fontFamily: 'var(--font-family)',
              color: selectedRate ? 'var(--fg-1)' : 'var(--fg-3)',
              cursor: 'pointer',
              '&:hover': { borderColor: 'var(--border-subtle)' },
            }}
          >
            <MaterialSymbol name="schedule" size={14} color="var(--fg-3)" />
            {selectedRate
              ? t('ownWork.PickedRate', {
                  role: selectedRate.roleRef.snapshot.name,
                  rate: formatAmount(selectedRate.rate),
                })
              : t('ownWork.NoRatePicked')}
            <MaterialSymbol name="expand_more" size={15} color="var(--fg-3)" />
          </Box>
          <Menu
            anchorEl={rateAnchor}
            open={rateAnchor !== null}
            onClose={() => setRateAnchor(null)}
          >
            {state.document.rateCard.length === 0 ? (
              <MenuItem disabled>{t('ownWork.NoRates')}</MenuItem>
            ) : (
              state.document.rateCard.map((entry) => (
                <MenuItem
                  key={entry.id}
                  selected={entry.id === selectedRate?.id}
                  data-automation-id="own-rate-option"
                  onClick={() => pickRate(entry)}
                >
                  {t('ownWork.RateOption', {
                    role: entry.roleRef.snapshot.name,
                    rate: formatAmount(entry.rate),
                  })}
                </MenuItem>
              ))
            )}
          </Menu>
        </Box>
      ) : null}

      {/* BR-11/D10: only time-based contracts carry a rate card. */}
      {hasRateCard ? <RateCardEditor /> : null}
      <Box sx={{ mb: 1 }} />
    </CategoryCard>
  );
};
